/**
 * `WooCommerceConnect` — the single source of truth for the WooCommerce
 * manual-key connect surface (ADR-0170). Both Settings → Integrations
 * (`SettingsIntegrations.tsx`) and the onboarding Install + verify step
 * (`WordPressInstallPanel.tsx`) render THIS module — the two surfaces
 * must not drift on validation, error copy, or POST shape.
 *
 * Exports:
 *   - `WooCommerceConnectForm` — the three-field form (Store URL,
 *     Consumer key, Consumer secret) + Connect button + bounded error
 *     copy. Calls `api.sites.connectWooCommerce()`. Clears the secret
 *     from component state on success; the plaintext must never linger
 *     in the DOM tree.
 *   - `WooCommerceInstructions` — the "In your WordPress admin, go to
 *     WooCommerce → Settings → Advanced → REST API…" prose block. Used
 *     above the form in Settings (and, in a compact variant, above the
 *     form in onboarding).
 *   - `wooConnectErrorCopy` — the ApiError → merchant copy mapping for
 *     the bounded server reason codes. Exported so callers that already
 *     surface toast copy can re-use the same phrasing.
 *
 * Voice: precise, technical, low-marketing. Every error branch names the
 * concrete cause the merchant needs to act on. No exclamation points.
 */

import { RefreshCw } from 'lucide-react';
import { useCallback, useState } from 'react';

import type { Site } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { useToast } from '../../app/providers.js';
import type { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type ThemeC = ReturnType<typeof useC>;

// ---------------------------------------------------------------------------
// Instructions block
// ---------------------------------------------------------------------------

export interface WooCommerceInstructionsProps {
  C: ThemeC;
}

export function WooCommerceInstructions({
  C,
}: WooCommerceInstructionsProps): JSX.Element {
  return (
    <div
      style={{
        fontFamily: sansFont,
        fontSize: 12,
        color: C.muted,
        lineHeight: 1.55,
      }}
    >
      In your WordPress admin, go to{' '}
      <span style={{ color: C.text }}>
        WooCommerce → Settings → Advanced → REST API
      </span>{' '}
      and click{' '}
      <span style={{ color: C.text }}>Add key</span>. Set{' '}
      <span style={{ color: C.text }}>Permissions</span> to{' '}
      <span style={{ color: C.text }}>Read/Write</span> if agents will
      place orders through Sill, or{' '}
      <span style={{ color: C.text }}>Read</span> for catalog and order
      lookups only. Generate the key, then paste the store URL, consumer
      key, and consumer secret below.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connect form
// ---------------------------------------------------------------------------

export interface WooCommerceConnectFormProps {
  C: ThemeC;
  site: Site;
  /** Called with the canonicalized store URL from the POST success
   *  response so the parent can stash it + refresh any downstream
   *  connector-status render. */
  onConnected: (site_url: string) => void;
  /** Optional prefix for input `id` attributes so multiple form
   *  instances (Settings + onboarding) on the same page never collide.
   *  Defaults to `'woo'`. */
  idPrefix?: string;
}

export function WooCommerceConnectForm({
  C,
  site,
  onConnected,
  idPrefix = 'woo',
}: WooCommerceConnectFormProps): JSX.Element {
  const api = useApi();
  const toast = useToast();
  const [siteUrl, setSiteUrl] = useState<string>(
    // Pre-fill with the verified domain over https — the server's L1
    // gate requires the URL to sit on this exact domain, so priming
    // the input keeps the merchant off the off_domain rejection path.
    `https://${site.domain}`,
  );
  const [consumerKey, setConsumerKey] = useState<string>('');
  const [consumerSecret, setConsumerSecret] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    !submitting
    && siteUrl.trim().length > 0
    && consumerKey.trim().length > 0
    && consumerSecret.trim().length > 0;

  const onSubmit = useCallback(async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.sites.connectWooCommerce(site.site_id, {
        site_url: siteUrl.trim(),
        consumer_key: consumerKey.trim(),
        consumer_secret: consumerSecret.trim(),
      });
      // Clear the secret from component state on success — the secret
      // does its job at POST time and MUST NOT linger in the DOM tree.
      setConsumerKey('');
      setConsumerSecret('');
      onConnected(res.site_url);
      toast({
        title: 'WooCommerce connected',
        body: 'The catalog and availability skills will light up once the first sync completes.',
        tone: 'lime',
      });
    } catch (err) {
      const message = err instanceof ApiError
        ? wooConnectErrorCopy(err)
        : 'Something went wrong. Try again.';
      setError(message);
      toast({
        title: 'Could not connect WooCommerce',
        body: message,
        tone: 'coral',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    api,
    canSubmit,
    consumerKey,
    consumerSecret,
    onConnected,
    site.site_id,
    siteUrl,
    toast,
  ]);

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit();
      }}
    >
      <WooField
        C={C}
        id={`${idPrefix}-site-url`}
        label="STORE URL"
        type="url"
        value={siteUrl}
        onChange={setSiteUrl}
        placeholder={`https://${site.domain}`}
        disabled={submitting}
        autoComplete="off"
        spellCheck={false}
      />
      <WooField
        C={C}
        id={`${idPrefix}-consumer-key`}
        label="CONSUMER KEY"
        type="text"
        value={consumerKey}
        onChange={setConsumerKey}
        placeholder="ck_…"
        disabled={submitting}
        autoComplete="off"
        spellCheck={false}
        mono
      />
      <WooField
        C={C}
        id={`${idPrefix}-consumer-secret`}
        label="CONSUMER SECRET"
        type="password"
        value={consumerSecret}
        onChange={setConsumerSecret}
        placeholder="cs_…"
        disabled={submitting}
        autoComplete="off"
        spellCheck={false}
        mono
      />
      {error !== null ? (
        <div
          role="alert"
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.coralFg,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      ) : null}
      <button
        type="submit"
        disabled={!canSubmit}
        className="inline-flex items-center gap-2 px-4 py-2"
        style={{
          background: canSubmit ? C.lime : C.surface2,
          color: canSubmit ? C.ink : C.muted,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
          border: 'none',
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          width: 'fit-content',
        }}
      >
        {submitting ? (
          <>
            <RefreshCw
              size={12}
              style={{ animation: 'spin 1.4s linear infinite' }}
            />
            Connecting…
          </>
        ) : (
          'Connect WooCommerce'
        )}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// WooField (input row)
// ---------------------------------------------------------------------------

interface WooFieldProps {
  C: ThemeC;
  id: string;
  label: string;
  type: 'text' | 'url' | 'password';
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  disabled: boolean;
  autoComplete?: string;
  spellCheck?: boolean;
  mono?: boolean;
}

function WooField({
  C,
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  disabled,
  autoComplete,
  spellCheck,
  mono,
}: WooFieldProps): JSX.Element {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        autoCapitalize="off"
        spellCheck={spellCheck}
        style={{
          width: '100%',
          maxWidth: 420,
          padding: '8px 10px',
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          color: C.text,
          fontFamily: mono === true ? monoFont : sansFont,
          fontSize: 12,
          outline: 'none',
          opacity: disabled ? 0.6 : 1,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error copy mapping
// ---------------------------------------------------------------------------

/** ADR-0170 §2 — map the bounded server reason codes onto human copy.
 *  Sill voice: precise, technical, low-marketing. Every branch names the
 *  concrete cause the merchant needs to act on. */
export function wooConnectErrorCopy(err: ApiError): string {
  const reason = err.server_reason;
  switch (err.kind) {
    case 'bad_request':
      if (reason === 'key_format_invalid') {
        return 'Consumer key and secret must be the ck_… and cs_… pair minted by WooCommerce.';
      }
      if (reason === 'body_invalid') {
        return 'The request was malformed. Check the store URL and key pair.';
      }
      // site_url_rejected + connect_probe_failed share the 400 status;
      // branch on the reason string the http-client parsed from the body.
      switch (reason) {
        case 'invalid_url':
          return 'The store URL is not a valid URL.';
        case 'unsupported_scheme':
          return 'The store URL must use https.';
        case 'userinfo_forbidden':
          return 'The store URL cannot contain a username or password.';
        case 'ip_literal_forbidden':
          return 'The store URL cannot be an IP address — use the store domain.';
        case 'port_forbidden':
          return 'The store URL cannot include a port.';
        case 'off_domain':
          return "The store URL must be on this site's verified domain.";
        case 'query_forbidden':
          return 'The store URL cannot include a query string.';
        case 'invalid_credentials':
          return 'WooCommerce rejected the key pair. Check the consumer key and secret.';
        case 'not_woocommerce':
          return 'No WooCommerce REST API found at that URL.';
        default:
          return 'The store URL or key pair was rejected. Check both and try again.';
      }
    case 'server_error':
      // 502 connect_probe_failed:site_unreachable arrives here — the
      // http-client's statusToKind maps 502 to server_error, and the
      // reason field carries the bounded probe outcome.
      if (reason === 'site_unreachable') {
        return 'The store could not be reached over HTTPS.';
      }
      return 'The connect probe failed. Try again in a moment.';
    case 'forbidden':
      return 'Your role cannot connect an integration. Ask an owner or admin.';
    case 'not_found':
      return 'This site is no longer accessible.';
    case 'rate_limited':
      return 'Too many connect attempts. Try again in a moment.';
    case 'service_unavailable':
      return 'The WooCommerce connect surface is unavailable on this deploy.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error. Check your connection and try again.';
    default:
      return err.message.length > 0 ? err.message : 'Unknown error.';
  }
}
